import React, { useEffect, useRef } from 'react'
import moment from 'moment'

import { c } from '../../translations/LibraryTranslationService'
import styles from './_filter.module.scss'
import {
  Button,
  DatePicker,
  hasValue,
  Popover,
  SearchBar,
  SelectDisplay,
  TrimText,
} from '../../module'
import {
  type ExposedFilterNameType,
  type ExposedFilterProps,
} from './ExposedFilter.models'
import RangeFilter from '../RangeFilter/RangeFilter'
import ExposedMultiSelectFilter from './ExposedMultiSelectFilter'
import { type MultiSelectDataItemBase } from '../MultiSelect/MultiSelect'

// Type guard for checking if an option matches the FilterType interface
function isFilterType<
  T extends MultiSelectDataItemBase & ExposedFilterNameType,
>(option: unknown): option is T {
  return (
    typeof option === 'object' &&
    option !== null &&
    'name' in option &&
    typeof (option as Record<string, unknown>).name === 'string'
  )
}

const ExposedFilter = <
  FilterType extends MultiSelectDataItemBase & ExposedFilterNameType,
>(
  filterValues: ExposedFilterProps<FilterType>,
): React.JSX.Element => {
  const {
    labelName,
    type,
    position,
    disableCloseOnScroll,
    onChangeCallout,
    stateName,
    isClearFilter,
    setIsClearFilter,
  } = filterValues
  const containerRef = useRef<HTMLDivElement>(null)

  // Clear filter when clear all is clicked
  useEffect(() => {
    if (isClearFilter) {
      if (type === 'date') onChangeCallout?.(stateName, undefined)
      if (type === 'search') onChangeCallout?.(stateName, '')
      setIsClearFilter?.(false)
    }
  }, [isClearFilter, setIsClearFilter, onChangeCallout, stateName, type])

  if (type === 'multi-select')
    return <ExposedMultiSelectFilter {...filterValues} />
  else if (type === 'date') {
    const { dateProps } = filterValues
    return (
      <Popover
        position={position ?? 'bottom-start'}
        disableCloseOnScroll={disableCloseOnScroll ?? true}
        noPadding
        maxWidth='max-content'
        popoverContent={
          <DatePicker
            {...dateProps}
            onDateChange={(value) => onChangeCallout?.(stateName, value)}
            showDateValue={true}
            isExposed={true}
          />
        }
      >
        {({ visible, setVisible }) => (
          <div
            ref={containerRef}
            className={`${styles.filterContainer} ${
              dateProps.selectedDate || dateProps.selectedDateRange
                ? styles.active
                : ''
            }`}
          >
            <Button as='unstyled' onClick={() => setVisible(!visible)}>
              <div
                className={`${styles.flexContainer} ${
                  dateProps.selectedDate || dateProps.selectedDateRange
                    ? styles.withClearButton
                    : ''
                }`}
              >
                <span
                  className={`${dateProps.selectedDate || dateProps.selectedDateRange ? styles.fontStyled : styles.fontNotSelected} ${styles.labelContainer}`}
                >
                  {labelName}
                </span>
                {dateProps.selectedDate || dateProps.selectedDateRange ? (
                  <div
                    className={`${styles.optionContainer} ${styles.optionContainerWithMaxWidth}`}
                  >
                    <TrimText
                      customClass={styles.optionName}
                      text={
                        dateProps.selectedDate
                          ? moment(dateProps.selectedDate).format(
                              'MMM DD, YYYY',
                            )
                          : dateProps.selectedDateRange
                            ? `${moment(dateProps.selectedDateRange?.from).format('MMM DD, YYYY')} - ${moment(dateProps.selectedDateRange.to).format('MMM DD, YYYY')}`
                            : ''
                      }
                    />
                  </div>
                ) : (
                  <span className={styles.textPurple}>{c('none')}</span>
                )}
              </div>
            </Button>
            {dateProps.selectedDate || dateProps.selectedDateRange ? (
              <Button
                as='unstyled'
                className={styles.clearButton}
                onClick={() => {
                  onChangeCallout?.(stateName, undefined)
                }}
              >
                {c('clear')}
              </Button>
            ) : null}
          </div>
        )}
      </Popover>
    )
  } else if (type === 'select') {
    const {
      selectProps: { options, selectedOption },
    } = filterValues

    return (
      <Popover
        position={position ?? 'bottom-start'}
        disableCloseOnScroll={disableCloseOnScroll ?? true}
        noPadding
        popoverContent={({ setVisible }) => (
          <div className={styles.selectContainer}>
            <SelectDisplay
              options={options.map((option) => ({
                ...option,
                label: 'name' in option ? option.name : '',
                name: 'name' in option ? option.name : '',
              }))}
              active={selectedOption ? 'name' : undefined}
              callout={(option) => {
                if (isFilterType<FilterType>(option)) {
                  onChangeCallout?.(stateName, option)
                  setVisible(false)
                }
              }}
            />
          </div>
        )}
      >
        {({ visible, setVisible }) => (
          <div
            ref={containerRef}
            className={`${styles.filterContainer} ${
              selectedOption ? styles.active : ''
            }`}
          >
            <Button as='unstyled' onClick={() => setVisible(!visible)}>
              <div
                className={`${styles.flexContainer} ${
                  selectedOption ? styles.withClearButton : ''
                }`}
              >
                <span
                  className={`${selectedOption ? styles.fontStyled : styles.fontNotSelected} ${styles.labelContainer}`}
                >
                  {labelName}
                </span>
                {selectedOption ? (
                  <div
                    className={`${styles.optionContainer} ${styles.optionContainerWithMaxWidth}`}
                  >
                    <TrimText
                      customClass={styles.optionName}
                      text={selectedOption.name}
                    />
                  </div>
                ) : (
                  <span className={styles.textPurple}>{c('none')}</span>
                )}
              </div>
            </Button>
            {selectedOption ? (
              <Button
                as='unstyled'
                className={styles.clearButton}
                onClick={() => onChangeCallout?.(stateName, undefined)}
              >
                {c('clear')}
              </Button>
            ) : null}
          </div>
        )}
      </Popover>
    )
  } else if (type === 'search') {
    const { searchBarProps } = filterValues
    return (
      <SearchBar
        {...searchBarProps}
        onChange={(value) => onChangeCallout?.(stateName, value)}
      />
    )
  } else if (type === 'range') {
    const { rangeProps } = filterValues
    return (
      <Popover
        position={position ?? 'bottom-start'}
        disableCloseOnScroll={disableCloseOnScroll ?? true}
        noPadding
        maxWidth='max-content'
        popoverContent={
          <RangeFilter
            selectedValues={rangeProps.selectedValues}
            onChangeCallout={(rangeValues) =>
              onChangeCallout?.(stateName, rangeValues)
            }
          />
        }
      >
        {({ visible, setVisible }) => (
          <div
            ref={containerRef}
            className={`${styles.filterContainer} ${
              hasValue(rangeProps.selectedValues.min) ||
              hasValue(rangeProps.selectedValues.max)
                ? styles.active
                : ''
            }`}
          >
            <Button as='unstyled' onClick={() => setVisible(!visible)}>
              <div
                className={`${styles.flexContainer} ${
                  hasValue(rangeProps.selectedValues.min) ||
                  hasValue(rangeProps.selectedValues.max)
                    ? styles.withClearButton
                    : ''
                }`}
              >
                <span
                  className={`${hasValue(rangeProps.selectedValues.min) || hasValue(rangeProps.selectedValues.max) ? styles.fontStyled : styles.fontNotSelected} ${styles.labelContainer}`}
                >
                  {labelName}
                </span>
                {hasValue(rangeProps.selectedValues.min) ||
                hasValue(rangeProps.selectedValues.max) ? (
                  <div
                    className={`${styles.optionContainer} ${styles.optionContainerWithMaxWidth}`}
                  >
                    <TrimText
                      customClass={styles.optionName}
                      text={
                        hasValue(rangeProps.selectedValues.min) &&
                        !hasValue(rangeProps.selectedValues.max)
                          ? `${c('min')}: ${rangeProps.selectedValues.min}`
                          : hasValue(rangeProps.selectedValues.max) &&
                              !hasValue(rangeProps.selectedValues.min)
                            ? `${c('max')}: ${rangeProps.selectedValues.max}`
                            : hasValue(rangeProps.selectedValues.min) &&
                                hasValue(rangeProps.selectedValues.max)
                              ? `${rangeProps.selectedValues.min} - ${rangeProps.selectedValues.max}`
                              : ''
                      }
                    />
                  </div>
                ) : (
                  <span className={styles.textPurple}>{c('none')}</span>
                )}
              </div>
            </Button>
            {hasValue(rangeProps.selectedValues.min) ||
            hasValue(rangeProps.selectedValues.max) ? (
              <Button
                as='unstyled'
                className={styles.clearButton}
                onClick={() => {
                  onChangeCallout?.(stateName, {
                    min: undefined,
                    max: undefined,
                  })
                }}
              >
                {c('clear')}
              </Button>
            ) : null}
          </div>
        )}
      </Popover>
    )
  } else {
    return <></>
  }
}

export default ExposedFilter
